/**
 * useOpenCallDocs - where the open calls for a test live (v9.5.3).
 *
 * A call waits in a ref Map, which is what the promise chain settles
 * against synchronously. In a session, on the GM's screen, every change
 * is ALSO written as `testCalls/<id>` (engine/narrative/openCall), and
 * the collection is watched: on mount, whatever is stored comes back
 * into the Map - a reload keeps the GM's open calls - and a call stored
 * with `closedAt` already set is one whose group line and riders have
 * fired: it is deleted, never fired again. Outside a session, or for a
 * player, the Map alone.
 *
 * @param {object} args - sessionId, isGm, mp (multiplayer; absent = local only)
 * @returns {{ get, put, remove, openCalls, ready }} - `openCalls` is the list for the
 *   GM's panel, refreshed on every change; `ready` is false until what is stored has
 *   been read once (always true where nothing is stored).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { readOpenCall } from '../engine/narrative/openCall';
import { diag, errorSummary } from '../diagnostics/log';

const parsed = (data) => {
  try {
    return typeof data === 'string' ? JSON.parse(data) : null;
  } catch {
    return null;
  }
};

/** Restore the stored calls into the Map: open ones kept, closed ones deleted. */
function restore(calls, entries, remove, { gone, restored }) {
  for (const { id, data } of entries) {
    const open = readOpenCall(id, parsed(data?.data));
    // Removed on this screen: a read that still lists it is of an earlier write (see `gone`).
    if (!open || gone.has(id)) continue;
    if (open.closedAt !== null) remove(id);
    else if (!calls.has(id)) {
      calls.set(id, open);
      restored.add(id);
    }
  }
  dropClosedElsewhere(calls, entries, restored);
}

/**
 * A call this screen RESTORED and a later read no longer lists was closed on another of the GM's
 * screens: it goes from this panel too. One made here is never dropped this way - its own write
 * may simply not have reached the list yet.
 */
function dropClosedElsewhere(calls, entries, restored) {
  const listed = new Set(entries.map((e) => e.id));
  for (const id of [...restored]) {
    if (listed.has(id)) continue;
    restored.delete(id);
    calls.delete(id);
  }
}

/** Watch the collection while synced: what is stored comes into the Map; what is closed goes. */
function useRestore(synced, mp, sessionId, { calls, gone, restored }, refresh, setRead) {
  useEffect(() => {
    if (!synced) return undefined;
    const drop = (id) =>
      mp.deleteTestCall(sessionId, id).catch((e) => diag.warn('story', 'a closed call stayed', errorSummary(e)));
    return mp.onTestCallsUpdate(sessionId, (read) => {
      restore(calls.current, read.entries ?? [], drop, { gone: gone.current, restored: restored.current });
      refresh();
      setRead(true);
    });
  }, [synced, mp, sessionId, calls, gone, restored, refresh, setRead]);
}

/**
 * What the store remembers between renders:
 * - `calls`: the open calls, which the promise chain settles against synchronously.
 * - `gone`: ids removed on this screen. A REMOVE IS NOT ORDERED AROUND THE READS: on PocketBase the
 *   event for a call's own earlier write arrived after it had closed, listed it as open, and the
 *   restore put it back on the GM's panel, waiting on someone who had rolled (2026-10-06). An id is
 *   a timestamp and is never used again, so the set is never pruned.
 * - `restored`: ids that came from the server, not from this screen (see `dropClosedElsewhere`).
 */
function useCallRefs() {
  return { calls: useRef(new Map()), gone: useRef(new Set()), restored: useRef(new Set()) };
}

const warn = (what) => (e) => diag.warn('story', `a call for a test could not be ${what}`, errorSummary(e));

export default function useOpenCallDocs({ sessionId, isGm, mp }) {
  const refs = useCallRefs();
  const { calls, gone } = refs;
  const [openCalls, setOpenCalls] = useState([]);
  const refresh = useCallback(() => setOpenCalls([...calls.current.values()]), [calls]);
  const synced = !!sessionId && !!isGm && !!mp;
  const put = useCallback(
    (open) => {
      calls.current.set(open.id, open);
      refresh();
      if (synced) mp.putTestCall(sessionId, open.id, open).catch(warn('saved'));
    },
    [synced, mp, sessionId, refresh, calls],
  );
  const remove = useCallback(
    (id) => {
      calls.current.delete(id);
      gone.current.add(id);
      refresh();
      if (synced) mp.deleteTestCall(sessionId, id).catch(warn('deleted'));
    },
    [synced, mp, sessionId, refresh, calls, gone],
  );
  const get = useCallback((id) => calls.current.get(id) ?? null, [calls]);
  const [read, setRead] = useState(false);
  useRestore(synced, mp, sessionId, refs, refresh, setRead);
  return { get, put, remove, openCalls, ready: !synced || read };
}
